// 🔧 communication_dashboard.js
// ✅ Purpose: Admin dashboard for the LGV Communication Engine
// ✅ Features:
// - Introduces the Communication Engine workspace
// - Shows high-level communication management areas
// - Provides entry point to Communication Studio
// - Shows future placeholders for Localization, SEO, Accessibility, Media, AI, and White Label
// - Uses standard LGV responsive container/card layout
// - No Firestore reads yet
// - No editing yet
// - No architecture changes

import React from "react";

import {
  Link,
} from "react-router-dom";

import SeoHead from "../../components/seo_head";

/**
 * Dashboard tool cards.
 *
 * Only Communication Studio is active for MVP.
 * Other tools are intentionally visible as future roadmap cards.
 */
const COMMUNICATION_TOOLS = [
  {
    title: "Communication Studio",
    icon: "💬",
    description:
      "Edit website page copy, buttons, headings, help text, and other platform communication.",
    actionLabel: "Open Studio",
    path: "/communication_studio",
    active: true,
  },
  {
    title: "Localization",
    icon: "🌎",
    description:
      "Prepare translated communication for future language and market expansion.",
    actionLabel: "Coming Soon",
    path: "",
    active: false,
  },
  {
    title: "SEO",
    icon: "🔍",
    description:
      "Manage searchable page titles, descriptions, Open Graph copy, and structured content.",
    actionLabel: "Coming Soon",
    path: "",
    active: false,
  },
  {
    title: "Accessibility",
    icon: "♿",
    description:
      "Review alt text, readable copy, accessibility notes, and inclusive communication.",
    actionLabel: "Coming Soon",
    path: "",
    active: false,
  },
  {
    title: "Media",
    icon: "🖼️",
    description:
      "Manage content images, localized image variants, captions, and alt text.",
    actionLabel: "Coming Soon",
    path: "",
    active: false,
  },
  {
    title: "White Label",
    icon: "🏢",
    description:
      "Prepare organization-specific communication, branding, and future override support.",
    actionLabel: "Coming Soon",
    path: "",
    active: false,
  },
  {
    title: "AI Assistance",
    icon: "✨",
    description:
      "Use the Knowledge Engine to draft, rewrite, translate, and improve communication.",
    actionLabel: "Coming Soon",
    path: "",
    active: false,
  },
];

/**
 * Simple stat cards for MVP.
 *
 * These are static until Firestore metrics are wired.
 */
const COMMUNICATION_STATS = [
  {
    title: "Runtime Bundles",
    value: "0",
    description: "Published page bundles ready for app reads.",
  },
  {
    title: "Content Items",
    value: "0",
    description: "Editable communication records.",
  },
  {
    title: "Locales",
    value: "1",
    description: "Default English source locale.",
  },
  {
    title: "Profiles",
    value: "1",
    description: "Default communication profile.",
  },
];

/**
 * ============================================================================
 * CommunicationDashboard
 * ============================================================================
 */

const CommunicationDashboard = () => {
  return (
    <>
      <SeoHead
        title="Communication Engine | Let's Go Vote"
        description="Communication Engine administration."
        noindex
      />

      <section
        id="top-content"
        className="container-fluid py-4"
      >
        <div
          className="container"
          style={{
            maxWidth: "1400px",
          }}
        >
          <div className="row mb-4">
            <div className="col-12 col-lg-8">
              <h1 className="display-5 mb-2">
                💬 Communication Engine
              </h1>

              <p className="lead text-muted mb-0">
                Manage the words, messages, metadata, accessibility text,
                media communication, and future localized content used across
                Let&apos;s Go Vote.
              </p>
            </div>
          </div>

          <div className="row g-4 mb-4">
            {COMMUNICATION_STATS.map((stat) => (
              <div
                key={stat.title}
                className="col-12 col-md-6 col-xl-3"
              >
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <div className="text-muted small">
                      {stat.title}
                    </div>

                    <div className="fs-3 fw-bold">
                      {stat.value}
                    </div>

                    <div className="small text-muted">
                      {stat.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-4">
            {COMMUNICATION_TOOLS.map((tool) => (
              <div
                key={tool.title}
                className="col-12 col-xl-6"
              >
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex gap-3">
                      <div
                        className="fs-2"
                        aria-hidden="true"
                      >
                        {tool.icon}
                      </div>

                      <div className="flex-grow-1">
                        <h2 className="h5 mb-2">
                          {tool.title}
                        </h2>

                        <p className="text-muted mb-3">
                          {tool.description}
                        </p>

                        {tool.active ? (
                          <Link
                            to={tool.path}
                            className="btn btn-outline-primary btn-sm"
                          >
                            {tool.actionLabel}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled
                          >
                            {tool.actionLabel}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card shadow-sm mt-4">
            <div className="card-header bg-white">
              <strong>Engine Status</strong>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <strong>Runtime Cache</strong>
                  <br />
                  Bundle-based
                </div>

                <div className="col-md-4 mb-3">
                  <strong>Default Locale</strong>
                  <br />
                  en-US
                </div>

                <div className="col-md-4 mb-3">
                  <strong>White Label Ready</strong>
                  <br />
                  Schema-ready
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CommunicationDashboard;